import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/macro';
import { LinearProgress } from '@material-ui/core';
import PropTypes from 'prop-types';
import { get, isEmpty, isNil } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useStyles } from '../../sso/LdapVerifyStyles';
import NgpFormButton from '../../../form/NgpFormButton';
import {
    ID_MAPPING,
    PASSWORD_FIELD,
    PORT_FIELD,
    USERNAME_FIELD,
    VCENTER_HOSTNAME_FIELD,
} from '../InfrastructureConstants';
import { getSystemTime } from '../../../../stores/SystemTimeStore';
import { useDataService } from '../../../../services/ApiDataService';
import { ErrorSpanMessage } from '../../../form/ValidationError';
import { to } from '../../../../utilities/PromiseUtilities';
import useActioningContext from '../../../../utilities/ActioningProviderUtilities';
import { VERIFICATION_CONFIG } from '../FieldConstraints';

const TEST_CONNECTIVITY_API_URL = '/query/admin/vmware/testConnectivity';
const CHECK_INTERVAL_MS = VERIFICATION_CONFIG.pollingInterval;
const MAX_TIMEOUT_MS = VERIFICATION_CONFIG.maxTimeout;
const MAX_COUNTOUT = VERIFICATION_CONFIG.maxAttempts;
const MILLISECONDS_PER_SECOND = VERIFICATION_CONFIG.timeFormat.millisPerSecond;
const DURATION_DECIMAL_PLACES = VERIFICATION_CONFIG.timeFormat.decimalPlaces;
const INITIAL_DURATION_SECONDS = 0;

const VERIFICATION_STATUS = {
    IDLE: 'idle',
    PROCESSING: 'processing',
    SUCCESS: 'success',
    FAILURE: 'failure',
    TIMEOUT: 'timeout',
    COUNTOUT: 'countout',
    CANCELLED: 'cancelled',
};

const STATUS_TEXT = {
    SUCCESS: 'Success',
    FAILURE: 'Failure',
    TIMEOUT: 'Time Out',
    COUNTOUT: 'Counter Out',
    CANCELLED: 'Cancelled',
    PROCESSING: 'Processing',
};

const STATUS_MESSAGES = {
    TRYING: 'Trying to connect to the vCenter.',
    INITIAL: 'Trying to connect vCenter configuration.',
    TIMEOUT: 'Connectivity test is timed out.',
    COUNTOUT: 'Connectivity test is counter out.',
    CANCELLED: 'Connectivity test is cancelled.',
    FAILED_START: 'Failed to start connectivity test.',
    FAILED_CONNECT: 'Failed to connect to vCenter.',
    VALIDATION_ERROR: 'Some fields are invalid',
};

/**
 * Creates initial verification result object
 * @returns {Object} Initial result with processing status
 */
const createInitialVerifyResult = () => ({
    status: `${STATUS_TEXT.PROCESSING} (${INITIAL_DURATION_SECONDS}s)`,
    statusMsg: STATUS_MESSAGES.INITIAL,
});

/**
 * Formats duration in seconds with specified decimal places
 * @param {number} durationMs - Duration in milliseconds
 * @returns {string} Formatted duration string (e.g., " (1.5s)")
 */
const formatDuration = (durationMs) => {
    if (isNil(durationMs) || durationMs < 0) {
        return ` (${INITIAL_DURATION_SECONDS}s)`;
    }
    const seconds = (durationMs / MILLISECONDS_PER_SECOND).toFixed(DURATION_DECIMAL_PLACES);
    return ` (${seconds}s)`;
};

/**
 * Safely extracts collector ID from ref
 * @param {Object} collectorRef - Ref containing collector data
 * @returns {string|null} Collector ID or null
 */
const getCollectorId = (collectorRef) => {
    return get(collectorRef, ['current', ID_MAPPING], null);
};

/**
 * Custom hook to manage vCenter connectivity verification state and polling logic
 * Handles polling, timeout, and cancellation of verification process
 */
const useVcenterVerification = ({ dataService, getValues, selectionCollectorRef }) => {
    const [verificationState, setVerificationState] = useState({
        status: VERIFICATION_STATUS.IDLE,
        result: null,
        hasAttemptedVerification: false,
    });

    const pollingRef = useRef({
        timeoutId: null,
        startTime: null,
        testId: null,
        isActive: false,
        isRequestInProgress: false,
        attemptCount: 0,
    });

    const calculateDuration = useCallback(() => {
        if (isNil(pollingRef.current.startTime)) {
            return formatDuration(0);
        }
        const durationMs = getSystemTime().valueOf() - pollingRef.current.startTime;
        return formatDuration(durationMs);
    }, []);

    const updateVerifyResult = useCallback((status, statusMsg) => {
        if (isEmpty(status) || isEmpty(statusMsg)) {
            return;
        }
        setVerificationState((prev) => ({
            ...prev,
            result: { status, statusMsg },
        }));
    }, []);

    const getTestParams = useCallback(() => {
        return {
            plcId: getCollectorId(selectionCollectorRef),
            portNumber: getValues(PORT_FIELD),
            username: getValues(USERNAME_FIELD),
            password: getValues(PASSWORD_FIELD),
            hostname: getValues(VCENTER_HOSTNAME_FIELD),
            testId: pollingRef.current.testId,
        };
    }, [getValues, selectionCollectorRef]);

    const stopPolling = useCallback(() => {
        if (pollingRef.current.timeoutId) {
            clearTimeout(pollingRef.current.timeoutId);
            pollingRef.current.timeoutId = null;
        }
        pollingRef.current.isActive = false;
        pollingRef.current.isRequestInProgress = false;
        pollingRef.current.attemptCount = 0;

        setVerificationState((prev) => ({
            ...prev,
            status: VERIFICATION_STATUS.IDLE,
        }));
    }, []);

    const checkVerificationLimits = useCallback(() => {
        const { startTime, attemptCount } = pollingRef.current;

        if (!isNil(startTime)) {
            const elapsedTime = getSystemTime().valueOf() - startTime;
            if (elapsedTime >= MAX_TIMEOUT_MS) {
                return {
                    exceeded: true,
                    type: 'timeout',
                    statusText: STATUS_TEXT.TIMEOUT,
                    message: STATUS_MESSAGES.TIMEOUT,
                };
            }
        }

        if (attemptCount >= MAX_COUNTOUT) {
            return {
                exceeded: true,
                type: 'countout',
                statusText: STATUS_TEXT.COUNTOUT,
                message: STATUS_MESSAGES.COUNTOUT,
            };
        }

        return { exceeded: false };
    }, []);

    const handleVerificationResponse = useCallback(
        (response) => {
            const { trying, success, detail } = response;

            if (trying) {
                updateVerifyResult(`${STATUS_TEXT.PROCESSING}${calculateDuration()}`, STATUS_MESSAGES.TRYING);
                return true; // Continue polling
            }

            const statusText = success ? STATUS_TEXT.SUCCESS : STATUS_TEXT.FAILURE;

            let message = detail;
            if (isEmpty(detail)) {
                message = success ? 'Connected successfully.' : STATUS_MESSAGES.FAILED_CONNECT;
            }

            updateVerifyResult(`${statusText}${calculateDuration()}`, message);
            return false; // Stop polling
        },
        [updateVerifyResult, calculateDuration]
    );

    const testConnectivity = useCallback(
        async (postBody) => {
            if (!pollingRef.current.isActive || pollingRef.current.isRequestInProgress) {
                return;
            }

            // Increment attempt count before checking limits
            pollingRef.current.attemptCount += 1;

            // Check if verification limits exceeded
            const limitCheck = checkVerificationLimits();
            if (limitCheck.exceeded) {
                updateVerifyResult(`${limitCheck.statusText}${calculateDuration()}`, limitCheck.message);
                stopPolling();
                return;
            }

            pollingRef.current.isRequestInProgress = true;

            const [error, response] = await to(dataService.post(TEST_CONNECTIVITY_API_URL, postBody));

            pollingRef.current.isRequestInProgress = false;

            if (!pollingRef.current.isActive) {
                return;
            }

            if (!isEmpty(error)) {
                updateVerifyResult(`${STATUS_TEXT.FAILURE}${calculateDuration()}`, STATUS_MESSAGES.FAILED_CONNECT);
                stopPolling();
                return;
            }

            const shouldContinuePolling = handleVerificationResponse(response);

            if (shouldContinuePolling && pollingRef.current.isActive) {
                pollingRef.current.timeoutId = setTimeout(() => {
                    const nextPostBody = getTestParams();
                    testConnectivity(nextPostBody).catch(() => {
                        // Error already handled in testConnectivity
                    });
                }, CHECK_INTERVAL_MS);
            } else {
                stopPolling();
            }
        },
        [
            dataService,
            calculateDuration,
            stopPolling,
            updateVerifyResult,
            getTestParams,
            checkVerificationLimits,
            handleVerificationResponse,
        ]
    );

    const startVerification = useCallback(() => {
        const currentTime = getSystemTime().valueOf();

        pollingRef.current.isActive = true;
        pollingRef.current.isRequestInProgress = false;
        pollingRef.current.startTime = currentTime;
        pollingRef.current.testId = currentTime;
        pollingRef.current.attemptCount = 0;

        setVerificationState({
            status: VERIFICATION_STATUS.PROCESSING,
            result: createInitialVerifyResult(),
            hasAttemptedVerification: true,
        });

        const postBody = getTestParams();
        testConnectivity(postBody).catch(() => {
            if (pollingRef.current.isActive) {
                updateVerifyResult(`${STATUS_TEXT.FAILURE}${calculateDuration()}`, STATUS_MESSAGES.FAILED_START);
                stopPolling();
            }
        });
    }, [getTestParams, testConnectivity, calculateDuration, updateVerifyResult, stopPolling]);

    const cancelVerification = useCallback(() => {
        updateVerifyResult(`${STATUS_TEXT.CANCELLED}${calculateDuration()}`, STATUS_MESSAGES.CANCELLED);
        stopPolling();
    }, [calculateDuration, stopPolling, updateVerifyResult]);

    useEffect(() => {
        const currentPollingRef = pollingRef.current;
        return () => {
            if (currentPollingRef.timeoutId) {
                clearTimeout(currentPollingRef.timeoutId);
            }
        };
    }, []);

    return {
        verificationState,
        startVerification,
        cancelVerification,
        isVerifying: verificationState.status === VERIFICATION_STATUS.PROCESSING,
    };
};

/**
 * VerifySection component handles vCenter connectivity verification UI and logic
 * Displays verify/cancel buttons and shows verification results
 */
const VerifySection = ({ dataService, errors, trigger, getValues, classes, selectionCollectorRef }) => {
    const { isActioning, setIsActioning } = useActioningContext();
    const [hasClickedVerify, setHasClickedVerify] = useState(false);

    const { verificationState, startVerification, cancelVerification, isVerifying } = useVcenterVerification({
        dataService,
        getValues,
        selectionCollectorRef,
    });

    // Sync verification state with global actioning state to disable save button during verification
    useEffect(() => {
        if (isVerifying !== isActioning) {
            setIsActioning(isVerifying);
        }
    }, [isVerifying, isActioning, setIsActioning]);

    // Check if collector selection is empty
    // Note: Direct computation without useMemo since it's a simple check
    // and we need to react to ref.current changes
    const isSelectionEmpty = isEmpty(getCollectorId(selectionCollectorRef));

    const hasFormErrors = useMemo(() => !isEmpty(errors), [errors]);

    const shouldShowValidationError = useMemo(() => {
        return hasClickedVerify && hasFormErrors;
    }, [hasClickedVerify, hasFormErrors]);

    const handleVerify = useCallback(async () => {
        setHasClickedVerify(true);

        const isValid = await trigger();
        if (!isValid) {
            return;
        }

        startVerification();
    }, [trigger, startVerification]);

    const handleCancel = useCallback(() => {
        cancelVerification();
    }, [cancelVerification]);

    const showCancelButton = verificationState.hasAttemptedVerification;
    const showResults = !isNil(verificationState.result);

    return (
        <div className={classes.verifySectionCompactBottom}>
            <div className={classes.verifyButtonContainerWrapper}>
                <NgpFormButton
                    disabled={isVerifying || isSelectionEmpty}
                    onClick={handleVerify}
                    aria-label="Verify vCenter connectivity"
                >
                    <Trans>Verify</Trans>
                </NgpFormButton>
                {showCancelButton && (
                    <NgpFormButton disabled={!isVerifying} onClick={handleCancel} aria-label="Cancel verification">
                        <Trans>Cancel</Trans>
                    </NgpFormButton>
                )}
            </div>

            {shouldShowValidationError && (
                <div role="alert">
                    <ErrorSpanMessage>
                        <Trans>{STATUS_MESSAGES.VALIDATION_ERROR}</Trans>
                    </ErrorSpanMessage>
                </div>
            )}

            {showResults && (
                <section className={classes.verifyTestResults} aria-live="polite" aria-label="Verification results">
                    {isVerifying && <LinearProgress aria-label="Verification in progress" />}
                    <div className={classes.verifyTestResult}>
                        <div className={classes.verifyTestResultText}>
                            <div className={classes.verifyTestResultTitle}>{verificationState.result.status}</div>
                            <div className={classes.verifyTestResultStatus}>{verificationState.result.statusMsg}</div>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

VerifySection.propTypes = {
    dataService: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    getValues: PropTypes.func.isRequired,
    trigger: PropTypes.func.isRequired,
    classes: PropTypes.object.isRequired,
    selectionCollectorRef: PropTypes.shape({
        current: PropTypes.object,
    }).isRequired,
};

/**
 * VcenterVerify component provides UI for verifying vCenter connectivity
 * Integrates with form context to validate inputs and test connection
 *
 * @param {Object} props - Component props
 * @param {Object} props.selectionCollectorRef - Ref containing selected collector mapping
 * @returns {JSX.Element} Verification UI component
 */
const VcenterVerify = ({ selectionCollectorRef }) => {
    const classes = useStyles();
    const dataService = useDataService();
    const {
        formState: { errors },
        trigger,
        getValues,
    } = useFormContext();

    return (
        <VerifySection
            dataService={dataService}
            errors={errors}
            trigger={trigger}
            getValues={getValues}
            classes={classes}
            selectionCollectorRef={selectionCollectorRef}
        />
    );
};

VcenterVerify.propTypes = {
    selectionCollectorRef: PropTypes.shape({
        current: PropTypes.object,
    }).isRequired,
};

export default VcenterVerify;
